import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InkProfileDetail } from "@shared-core/data/manufacturing-setup-types";
import { formatSyncedFrom } from "@shared-core/domain/manufacturing-setup-presentation";
import { paths } from "@shared-core/routing/routes";
import { SettingsBreadcrumbs } from "../app/components/patterns/SettingsBreadcrumbs";
import { Badge } from "../app/components/primitives/badge";
import { EngineUseFieldTable } from "../app/components/domain/manufacturing/EngineUseFieldTable";

/** The breadcrumb trail shared by every state of this page — Settings ›
 *  Catalogue › Live costing › Manufacturing setup, plus this ink profile's
 *  own identity once it has loaded. */
function crumbs(leaf?: string) {
  const base = [
    { label: "Settings", to: paths.settingsIntegrations() },
    { label: "Catalogue", to: paths.settingsCatalogues() },
    { label: "Live costing", to: paths.settingsLiveCostingStatus() },
  ];
  return leaf
    ? [
        ...base,
        {
          label: "Manufacturing setup",
          to: paths.settingsLiveCostingManufacturingSetup(),
        },
        { label: leaf },
      ]
    : [...base, { label: "Manufacturing setup" }];
}

export function ManufacturingSetupInkProfileDetailPage() {
  const adapter = useDataAdapter();
  const { inkProfileId: inkProfileIdParam } = useParams<{
    inkProfileId: string;
  }>();
  const inkProfileId = inkProfileIdParam ? Number(inkProfileIdParam) : NaN;

  const [detail, setDetail] = useState<InkProfileDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const load = useCallback(() => {
    if (Number.isNaN(inkProfileId)) return;
    setLoadError(false);
    adapter
      .getManufacturingInkProfile(inkProfileId)
      .then((d) => {
        if (d === null) {
          setNotFound(true);
          return;
        }
        setDetail(d);
      })
      .catch(() => setLoadError(true));
  }, [adapter, inkProfileId]);

  useEffect(() => {
    load();
  }, [load]);

  if (Number.isNaN(inkProfileId) || notFound) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">
          No such ink profile. The link may be out of date.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (loadError) {
    return (
      <BreadcrumbsOnly>
        <p className="text-destructive">
          Couldn&rsquo;t load this ink profile. Please try again.
        </p>
      </BreadcrumbsOnly>
    );
  }

  if (!detail) {
    return (
      <BreadcrumbsOnly>
        <p className="text-muted-foreground">Loading...</p>
      </BreadcrumbsOnly>
    );
  }

  return (
    <div className="space-y-6">
      <SettingsBreadcrumbs crumbs={crumbs(detail.code)} />

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold text-foreground">
          Ink profile · {detail.code}
        </h1>
        <p className="text-sm text-muted-foreground">
          {detail.name}
          {detail.isLegacy && (
            <Badge variant="neutral" className="ml-2">
              Legacy
            </Badge>
          )}
        </p>
        <p className="text-xs text-muted-foreground">
          {formatSyncedFrom(detail.sync)}
        </p>
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Fields
        </h2>
        <EngineUseFieldTable
          fields={detail.fields}
          emptyMessage="No fields captured for this ink profile."
        />
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          The engine&rsquo;s ink constants for this family
        </h2>
        <EngineUseFieldTable
          fields={detail.engineConstants}
          emptyMessage="No engine constants captured for this ink profile."
        />
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
          Suitable on
        </h2>
        {detail.suitableOn.length === 0 ? (
          <p className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            No cost centres are recorded as suitable for this ink profile.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <tbody className="divide-y divide-border">
                {detail.suitableOn.map((press) => (
                  <tr key={press.costCentreId}>
                    <td className="px-3 py-2 font-medium">
                      <Link
                        to={paths.settingsLiveCostingCostCentreDetail({
                          costCentreId: press.costCentreId,
                        })}
                        className="text-[color:var(--color-primary)] hover:underline"
                      >
                        {press.costCentreId} {press.code}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {press.name}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {press.site}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function BreadcrumbsOnly({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-4">
      <SettingsBreadcrumbs crumbs={crumbs()} />
      {children}
    </div>
  );
}
